import { Box, Grid, MenuItem } from "@mui/material";
import { FieldArray, Form, Formik } from "formik";
import React from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../app/api/axiosError";
import {
  addClientPcFdpMapping,
  editClientPcFdpMapping,
} from "../../../app/features/client/clientSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { FdpResponse } from "../../../app/models/responseModels/FdpResponse";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import SubmitModalWithFormik from "../../../components/common/modalPopup/SubmitModalWithFormik";
import { TransformedData } from "../associated parcelcenters/AssociatedParcelCenterGrid";
import { InitialValues } from "./AddParcelCenterMappingModal";

type EditParcelCenterMappingModalProps = {
  clientId: number;
  rowData: TransformedData;
  closeModal: CallableFunction;
  onCompleted: CallableFunction;
};

const EditParcelCenterMappingModal: React.FC<
  EditParcelCenterMappingModalProps
> = ({ clientId, rowData, closeModal, onCompleted }) => {
  const { parcelCenterList } = useSelector(
    (state: RootState) => state.parcelCenter
  );

  const { authResponse } = useSelector((state: RootState) => state.login);

  const dispatch = useAppDispatch();

  const validationSchema = Yup.object().shape({
    clientParcelCenterFdpMappings: Yup.array().of(
      Yup.object().shape({
        parcelCenterId: Yup.number().required(
          "Parcel Center selection is required"
        ),
        fdpIds: Yup.array()
          .of(Yup.number())
          .required("FDP selection is required")
          .min(1, "Pick at least one fdp"),
      })
    ),
  });
  const initialValues: InitialValues = {
    clientId: clientId,
    createdBy: authResponse?.userSettings?.userName ?? "",
    updatedBy: authResponse?.userSettings?.userName ?? "",
    clientParcelCenterFdpMappings: [
      {
        parcelCenterId: rowData.parcelCenter.parcelCenterId,
        fdpIds: rowData.fdps.map((e) => e.id),
      },
    ],
  };

  const handleSubmit = async (
    values: InitialValues,
    { setFieldError }: any
  ) => {
    console.log(values);
    try {
      const response = await dispatch(editClientPcFdpMapping(values)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, { toastId: "editClientPcFdpSuccess" });
        onCompleted && onCompleted(values);
      } else {
        toast.error(response.message!, { toastId: "editClientPcFdpError" });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "editClientPcFdpException",
      });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting, setFieldError }) => {
        setSubmitting(true);
        handleSubmit(values, { setFieldError }).finally(() =>
          setSubmitting(false)
        );
      }}
    >
      {({ values, isSubmitting, setFieldValue }) => {
        return (
          <SubmitModalWithFormik
            title="Edit to Parcel Center"
            closeSubmitModal={closeModal}
            allowSubmit={true}
            size="lg"
            isLoading={isSubmitting}
            submitButtonName="Add"
            closeModalAfterSubmit={false}
          >
            <Form>
              <FieldArray name="clientParcelCenterFdpMappings">
                {({ push, remove }) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Grid
                      container
                      rowSpacing={2}
                      columnSpacing={2}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <FormikTextField
                          select
                          margin="normal"
                          id={`clientParcelCenterFdpMappings[0].parcelCenterId`}
                          name={`clientParcelCenterFdpMappings[0].parcelCenterId`}
                          label="Parcel Center"
                          size="small"
                          fullWidth
                          variant={"outlined"}
                          SelectProps={{
                            MenuProps: {
                              PaperProps: { style: { maxHeight: 150 } },
                            },
                          }}
                          required
                          isDisabled={true}
                        >
                          {parcelCenterList.map((e) => (
                            <MenuItem
                              key={e.parcelCenterId}
                              value={e.parcelCenterId}
                            >
                              {e.parcelCenterName}
                            </MenuItem>
                          ))}
                        </FormikTextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <FormikTextField
                          select
                          margin="normal"
                          id={`clientParcelCenterFdpMappings[0].fdpIds`}
                          name={`clientParcelCenterFdpMappings[0].fdpIds`}
                          label="FDP(s)"
                          size="small"
                          fullWidth
                          variant={"outlined"}
                          SelectProps={{
                            MenuProps: {
                              PaperProps: { style: { maxHeight: 150 } },
                            },
                            multiple: true,
                          }}
                          required
                        >
                          {(
                            parcelCenterList.find(
                              (e) =>
                                e.parcelCenterId ==
                                values.clientParcelCenterFdpMappings[0]
                                  .parcelCenterId
                            )?.fdps || ([] as FdpResponse[])
                          ).map((e, i) => (
                            <MenuItem key={(e.id + i).toString()} value={e.id}>
                              {e.companyName}
                            </MenuItem>
                          ))}
                        </FormikTextField>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </FieldArray>
            </Form>
          </SubmitModalWithFormik>
        );
      }}
    </Formik>
  );
};

export default EditParcelCenterMappingModal;
